import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { UserDb } from 'services/user-db.service';
import { deepClone, deepCompareEQ, deepOverwrite } from '@cf-shared/deep-helpers';
import { AuthService } from 'services/auth.service';
import { VldGroupDirective } from 'directives/vld-group/vld-group';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { sortBy, remove, isEqual } from 'lodash-es';
import { SettingsService } from 'services/settings.service';
import { formatMeasure } from 'util/formatting';
import { TutorialController } from 'providers/tutorial-controller';
import { sleep } from '@cf-shared/shared-util';
import { EquipmentType, Equipment, EquipmentElem_Plates, isEquipmentIdTemplate, countSimilarValues } from '@cf-shared/data-model/equipment';
import { Config_Pairs, Config_Plates } from '@cf-shared/measure-configs';
import { FitnessState } from '@cf-shared/data-model/user-state';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { getEquipmentTemplates } from './equipment-templates';
import { duplicateEquipment } from 'services/equipment-helpers';
import { EquipmentEditPageParams } from './equipment-edit-interface';
import { IEquipmentScannerPage, EquipmentScannerPageResult } from 'pages/equipment-scanner-page/equipment-scanner-page-interface';
import { UnsavedChangesGuard, UnsavedChangesGuardProvider, CanLeaveResult } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { LoggingService } from 'services/logging.service';
import { RangeWeightGroup, RangeWeightKind, FIXED_WEIGHTS, STACK_WEIGHTS, sortRangeWeights, groupRangeWeights, ungroupRangeWeights, expandRangeGroup, normalizeRangeWeightsForSave, rangeGroupsNeedCleanUp } from './equipment-range-weights';

interface PlatesGroup {
  weight_kg: number;
  count: number;
}

// dumbbells and weight stacks share one code path; see equipment-range-weights.ts
const RANGE_WEIGHT_KINDS: RangeWeightKind<unknown>[] = [FIXED_WEIGHTS, STACK_WEIGHTS];

@Component({
  selector: 'page-equipment-edit',
  templateUrl: 'equipment-edit-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ModalPageMarker]
})
export class EquipmentEditPage extends CsPage<EquipmentEditPageParams> {
  protected LL = LL;
  @ViewChild(VldGroupDirective, { static: true }) vldGroup: VldGroupDirective;

  protected EquipmentType = EquipmentType; // needed by the page template

  // these are used by vld-* components
  progressStep = 0;
  totalSteps: number;
  public isModified = false;

  protected title = '';
  protected createMode = false;
  protected equipmentId: string;
  protected fitnessState: FitnessState;
  protected equipment: Equipment;
  protected usedByText: string;
  protected collars2x_text = '';
  protected plateGroups: PlatesGroup[] = [];
  protected fixedGroups: RangeWeightGroup[] = [];
  protected stackedGroups: RangeWeightGroup[] = [];

  private docWatcher: DocWatcher;
  protected equipmentInital: Equipment;
  private unsavedChangesGuard: UnsavedChangesGuard<void>;

  // template selection for create mode
  protected templates: Equipment[] = [];
  protected selectedTemplateId: string;
  protected suggestedTemplateId: string;

  // step 1 is when in create mode but no equipment yet
  get isStep1(): boolean {
    return this.createMode && !this.equipment;
  }

  // Scan button is shown when either:
  //   - no draft exists yet (step-1 create mode) — scanner runs in auto-detect mode and synthesises
  //     the equipment from the AI's detectedType, OR
  //   - a draft exists with one of the four supported types.
  // Hidden once a draft of an unsupported type (Manual, Unknown) is loaded.
  get showScanButton(): boolean {
    if (!this.equipment) return true;
    return this.equipment.type === EquipmentType.Bar
        || this.equipment.type === EquipmentType.Plates
        || this.equipment.type === EquipmentType.Stack
        || this.equipment.type === EquipmentType.Fixed;
  }

  // used to focus the rows on mobile
  focusedRow: number = null;

  get plateCountLabel(): TranslatedString {
    if (this.equipment?.type === EquipmentType.Bar) {
      return LL.equipment.labels.numberOfPairs();
    }
    return LL.equipment.labels.numberOfPlates();
  }
  blockRowEdits: boolean = false;

  constructor(
    public navCtrl: NavController,
    public userDb: UserDb,
    public auth: AuthService,
    private settings: SettingsService,
    private tutorialCtrl: TutorialController,
    private unsavedChangesGuardProvider: UnsavedChangesGuardProvider,
    private logging: LoggingService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  onScanClick() {
    void this.navCtrl.pushAsync(IEquipmentScannerPage, {
      returnCallback: 'onEquipmentScanResult',
      // pass the current draft when present; in step-1 create mode there's nothing to scan into
      // and the scanner runs in auto-detect mode
      local: this.equipment ? { equipmentDraft: deepClone(this.equipment) } : {},
    });
  }

  async onEquipmentScanResult(_ctx: unknown, result: EquipmentScannerPageResult) {
    if (!result?.updatedDraft || this.viewDestroyed) return;
    // auto-detect mode (started in step 1): the returned draft has a freshly-synthesised type and
    // an empty id (`freshEquipmentOfType('', t)`) — bridge into the same shape loadAsync would
    // have produced by picking the matching template, then carry the freshly-generated id over
    // onto the scan-result draft so we don't overwrite it with `''`.
    if (!this.equipment) {
      const detected = result.updatedDraft.type;
      const template = this.templates.find(t => t.type === detected);
      if (template) {
        this.selectedTemplateId = template.id;
        // initialise via the same path the dropdown takes — this populates this.equipment with a
        // freshly-cloned template equipment that has a real generated id
        this.onTemplateSelected(template.id);
        // carry the generated id from the template path onto the scan draft so saves produce a
        // valid doc; the scan draft otherwise has id === '' from freshEquipmentOfType
        const generatedId = (this.equipment as Equipment | undefined)?.id;
        if (generatedId && !result.updatedDraft.id) {
          result.updatedDraft.id = generatedId;
        }
      }
    }
    // merge the returned draft into local state without saving — user reviews and saves explicitly
    this.equipment = result.updatedDraft;
    this.isModified = !deepCompareEQ(this.equipment, this.equipmentInital);
    this.sortWeights();
    this.groupWeights();
    this.updateTexts();
    this.cdr.markForCheck();
  }

  async loadAsync() {
    this.fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    if (!this.fitnessState) {
      return;
    }
    let equipment: Equipment = this.fitnessState.allEquipment[this.equipmentId];
    if (!equipment) {
      return;
    }
    // copy on load, so we can properly cancel, since fitness state is a global persistent singleton!
    this.equipment = deepClone(equipment);
    this.equipmentInital = deepClone(equipment);

    this.sortWeights();
    this.groupWeights();

    this.updateTexts();

    this.cdr.markForCheck();
  }

  /**
   * Save on the way out, when the user picks Save in the leave dialog.
   *
   * `cs-nav` runs the guard inside a try/catch that turns any throw into "you
   * may not leave" plus a console line nobody sees in production: no telemetry,
   * no message, and a page that just refuses to close. So report the failure
   * before letting it travel on. The rethrow is what keeps the page open with
   * the user's weights still in it, which is right — it just must not be silent.
   */
  private async saveFromLeaveDialogAsync() {
    try {
      await this.saveAsync();
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.unsavedChanges.errors.saveFailed());
      throw err;
    }
  }

  async saveAsync() {
    if (!this.fitnessState) {
      return;
    }

    this.ungroupWeights();
    this.sortWeights();

    // normalize eventual dummy values
    let configElemPlates = this.equipment as EquipmentElem_Plates;
    if (configElemPlates.plates) {
      configElemPlates.plates = configElemPlates.plates.filter(p => p.weight_kg > 0);
    }
    for (let kind of RANGE_WEIGHT_KINDS) {
      normalizeRangeWeightsForSave(kind, this.equipment);
    }

    this.fitnessState.allEquipment = this.fitnessState.allEquipment || {};
    this.fitnessState.allEquipment[this.equipment.id] = deepClone(this.equipment);

    await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdr.markForCheck();
      });
    }

    this.equipmentId = this.navParams.data.equipment;
    this.createMode = this.navParams.data.mode === 'create';
    this.suggestedTemplateId = this.navParams.data.suggestedTemplateId;

    this.fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);

    // load templates for dropdown (always needed now)
    this.templates = getEquipmentTemplates(this.settings.locale);

    // in edit mode, load existing equipment
    if (!this.createMode) {
      await this.loadAsync();
      if (!this.equipment || isEquipmentIdTemplate(this.equipment.id)) {
        return this.auth.failPageGuardAsync(`Unknown or invalid equipment id '${this.equipmentId}'`);
      }
      this.title = `Edit ${this.equipment.name}`;

      // set selectedTemplateId based on equipment's template or type
      const templateId = this.equipment.basedOnTemplateId;
      const matchingTemplate = this.templates.find(t => t.id === templateId);
      if (matchingTemplate) {
        this.selectedTemplateId = templateId;
      } else {
        // fallback: find template by equipment type
        const template = this.templates.find(t => t.type === this.equipment.type);
        this.selectedTemplateId = template?.id;
      }
    } else {
      // create mode - check if equipment ID was passed (legacy) or just templateId (new)
      if (this.equipmentId && this.fitnessState.allEquipment?.[this.equipmentId]) {
        // legacy: equipment was pre-created by search page
        await this.loadAsync();
        // ensure selectedTemplateId points to an actual template
        const templateId = this.equipment.basedOnTemplateId;
        const matchingTemplate = this.templates.find(t => t.id === templateId);
        if (matchingTemplate) {
          this.selectedTemplateId = templateId;
        } else {
          // fallback: find template by equipment type
          const template = this.templates.find(t => t.type === this.equipment.type);
          this.selectedTemplateId = template?.id;
        }
      } else if (this.navParams.data.templateId) {
        // new: just template ID passed, create in-memory equipment
        this.selectedTemplateId = this.navParams.data.templateId;
        this.createEquipmentFromTemplate(this.selectedTemplateId);
      }
      // else: no template - show step 1 (empty state)

      this.title = LL.equipment.titles.createEquipment();
      this.isModified = !this.isStep1; // in create mode, allow save once template selected
    }

    // Ask before the page closes on unsaved weights (#5058). This page replaces
    // the draft it is editing — a different machine template, or a scan result,
    // swaps in a whole new object — so the guard cannot watch that object by
    // reference. It gets the page's own comparison instead.
    this.unsavedChangesGuard = this.unsavedChangesGuardProvider.createGuard<void>(
      [],
      async () => { await this.saveFromLeaveDialogAsync(); },
      { hasUnsavedChanges: () => this.hasUnsavedEquipmentEdits() },
    );

    this.cdr.markForCheck();
  }

  /**
   * True when the draft on screen differs from what is stored.
   *
   * Weights are shown grouped ("4 x 20 kg" rather than four rows), so ungroup
   * first or the comparison is against a different shape — this is the same
   * step `somethingChanged` takes, and for the same reason. Ungrouping does not
   * re-group, so the display the user is looking at is left alone.
   */
  private hasUnsavedEquipmentEdits(): boolean {
    if (!this.equipment) return false;
    // In create mode the whole machine is unsaved by definition — nothing has
    // been written to the fitness state yet — so leaving loses it whether or not
    // the user changed anything after picking the template. Comparing against
    // `equipmentInital` here would answer "nothing changed", because create mode
    // seeds that baseline from the very object it is comparing. This matches what
    // the page's own `isModified` already says, which drives the Save button.
    if (this.createMode) return true;
    if (!this.equipmentInital) return false;
    this.ungroupWeights();
    return !deepCompareEQ(this.equipment, this.equipmentInital);
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the browser back
   * button, Android's hardware back button or a swipe back. Without it those
   * three ways out threw away every plate, stack step and fixed weight the user
   * had just entered, with nothing asked (#5058).
   */
  async ionViewCanLeave(): Promise<CanLeaveResult<void>> {
    // the Accept and Cancel buttons arm this once they have actually carried out
    // their decision — Accept having saved, Cancel having put the draft back — so
    // the guard does not ask a second time
    if (this.leaveWithoutAsking.consume()) return true;
    // the guard is built at the end of page entry; before that there is nothing
    // the user could have changed
    if (!this.unsavedChangesGuard) return true;
    return this.unsavedChangesGuard.confirmLeaveAsync();
  }

  // create in-memory equipment from template (NOT saved to fitnessState yet)
  createEquipmentFromTemplate(templateId: string) {
    const template = this.templates.find(t => t.id === templateId);
    if (!template) return;

    // create in-memory equipment - duplicateEquipment generates new ID
    const newEquipment = duplicateEquipment(this.fitnessState, template);

    // set as current equipment (in-memory only)
    this.equipmentId = newEquipment.id;
    this.equipment = deepClone(newEquipment);
    this.equipmentInital = deepClone(newEquipment);

    // initialize UI
    this.sortWeights();
    this.groupWeights();
    this.updateTexts();

    this.isModified = true;
    this.cdr.markForCheck();
  }

  // handle template dropdown change
  onTemplateSelected(templateId: string) {
    if (!templateId) return;
    this.selectedTemplateId = templateId;
    this.createEquipmentFromTemplate(templateId);
  }

  async ionViewDidEnter() {
    // only show tutorial when in step 2 (equipment already selected)
    if (this.createMode && !this.isStep1) {
      await this.tutorialCtrl.playTutorialAsync({
        id: 'equipment-edit.create',
        steps: [
          {
            highlightSelectors: ['.config-name'],
            highlightScaler: 0.7,
            message: LL.equipment.tutorial.nameEquipment(),
          },
          {
            highlightSelectors: [
              '.weight-type-body-content :nth-child(1) .input-item',
              '.weight-type-body-content :nth-child(2) .input-item',
              '.weight-type-body-content :nth-child(3) .input-item',
              '.weight-type-body-content :nth-child(4) .input-item',
              '.weight-type-body-content :nth-child(5) .input-item',
            ],
            allowUnmatchedSelectors: true,  // not all rows have inputs, depending on the equipment type
            message: LL.equipment.tutorial.adjustValues(),
          },
          {
            highlightSelectors: ['page-equipment-edit cs-icon.checkmark'],
            highlightScaler: 2.0,
            message: LL.equipment.tutorial.saveEquipment(),
          },
        ]
      })
    }
  }

  addStacked() {
    this.addRangeGroup(STACK_WEIGHTS);
  }

  removeStacked(s: RangeWeightGroup) {
    this.removeRangeGroup(STACK_WEIGHTS, s);
  }

  addFixed() {
    this.addRangeGroup(FIXED_WEIGHTS);
  }

  removeFixed(f: RangeWeightGroup) {
    this.removeRangeGroup(FIXED_WEIGHTS, f);
  }

  // add an empty from / to / increment row for dumbbells or a weight stack
  private addRangeGroup<E>(kind: RangeWeightKind<E>) {
    if (this.blockRowEdits) {
      return;
    }

    if (this.equipment.type === kind.equipmentType) {
      this.rangeGroupsOf(kind).push({ firstWeight_kg: undefined, lastWeight_kg: undefined, stepWeight_kg: undefined });
    }

    this.someWeightChanged();
  }

  // remove one from / to / increment row for dumbbells or a weight stack
  private removeRangeGroup<E>(kind: RangeWeightKind<E>, group: RangeWeightGroup) {
    if (this.blockRowEdits) {
      return;
    }

    if (this.equipment.type === kind.equipmentType) {
      remove(this.rangeGroupsOf(kind), i => i === group);
    }

    this.someWeightChanged();
  }

  // the page's display groups for one range-grouped kind; the template binds to these two fields by name
  private rangeGroupsOf<E>(kind: RangeWeightKind<E>): RangeWeightGroup[] {
    return kind.equipmentType === EquipmentType.Fixed ? this.fixedGroups : this.stackedGroups;
  }

  // replace the page's display groups for one range-grouped kind
  private setRangeGroupsOf<E>(kind: RangeWeightKind<E>, groups: RangeWeightGroup[]) {
    if (kind.equipmentType === EquipmentType.Fixed) {
      this.fixedGroups = groups;
    } else {
      this.stackedGroups = groups;
    }
  }

  addPlate() {
    if (this.blockRowEdits) {
      return;
    }

    if (
      this.equipment.type === EquipmentType.Plates ||
      this.equipment.type == EquipmentType.Bar ||
      this.equipment.type === EquipmentType.Stack ||
      this.equipment.type === EquipmentType.Fixed
    ) {
      let count = 1;
      if (this.equipment.type === EquipmentType.Plates) {
        count = 2;  // plates are usually added in pairs because the same ones are used on the barbell as well
      }
      this.plateGroups.push({ weight_kg: undefined, count: count });
    }

    this.someWeightChanged();
  }

  removePlate(p: any) {
    if (this.blockRowEdits) {
      return;
    }

    if (
      this.equipment.type === EquipmentType.Plates ||
      this.equipment.type == EquipmentType.Bar ||
      this.equipment.type === EquipmentType.Stack ||
      this.equipment.type === EquipmentType.Fixed
    ) {
      remove(this.plateGroups, f => f === p);
    }

    this.someWeightChanged();
  }

  // used by [validity] property in vld-error component in the form
  validity(field: string): string {
    if (field === 'name') {
      if (this.equipment.name === '') {
        return LL.equipment.validation.invalidName();
      }
      return null;
    }
    if (this.equipment.type === EquipmentType.Manual) {
      switch (field) {
        case 'weight_inc':
          {
            if (this.equipment.weight_inc_kg == null || this.equipment.weight_min_kg == null) {
              return LL.equipment.validation.incrementHelp();
            }
          }
          break;
        case 'weight_min':
          {
            if (this.equipment.weight_min_kg == null) {
              return LL.equipment.validation.minimumHelp();
            }
          }
          break;
        default:
          return LL.equipment.validation.unknownField();
      }
      return null;
    }
    if (this.equipment.type === EquipmentType.Bar) {
      switch (field) {
        case 'bar': {
          if (!(this.equipment.bar_kg > 0)) {
            return LL.equipment.validation.barWeightRequired();
          }
          return null;
        }
        case 'collar':
          {
          }
          break;
        default:
          return LL.equipment.validation.unknownField();
      }
      return null;
    }
    return LL.equipment.validation.unknownEquipmentType();
  }

  sortWeights() {
    let configPlates = this.equipment as EquipmentElem_Plates;
    if (configPlates.plates && configPlates.plates.length > 0) {
      configPlates.plates.sort((a, b) => a.weight_kg - b.weight_kg);
    }

    for (let kind of RANGE_WEIGHT_KINDS) {
      sortRangeWeights(kind, this.equipment);
    }

    this.cdr.markForCheck();
  }

  // group the plates by weight, so we can show them in a more compact way
  groupWeights() {
    this.plateGroups = [];
    if ('plates' in this.equipment || this.plateGroups.length > 0) {
      let configPlates = this.equipment as EquipmentElem_Plates;
      if (configPlates.plates) {
        let similar = countSimilarValues(configPlates.plates.map(p => p.weight_kg));
        this.plateGroups = similar.map(s => ({ weight_kg: s.value, count: s.count }));
      }
    }
    for (let kind of RANGE_WEIGHT_KINDS) {
      let elements = kind.getElements(this.equipment);
      // if the list is missing there is nothing to group, and the page keeps whatever groups it had
      if (!elements) {
        continue;
      }
      this.setRangeGroupsOf(kind, groupRangeWeights(elements.map(e => kind.weightOf(e))));
    }
  }
  // reflect the group changes back to the equipment
  ungroupWeights() {
    if ('plates' in this.equipment || this.plateGroups.length > 0) {
      let configPlates = this.equipment as EquipmentElem_Plates;
      configPlates.plates = [];
      for (let g of this.plateGroups) {
        for (let i = 0; i < g.count; i++) {
          configPlates.plates.push({ weight_kg: g.weight_kg });
        }
      }
    }
    for (let kind of RANGE_WEIGHT_KINDS) {
      if (kind.isPresentOn(this.equipment) || this.rangeGroupsOf(kind).length > 0) {
        kind.setElements(this.equipment, ungroupRangeWeights(this.rangeGroupsOf(kind)).map(w => kind.makeElement(w)));
      }
    }
  }

  cleanUp() {
    this.ungroupWeights();
    this.sortWeights();
    this.groupWeights();
    this.cdr.markForCheck();
  }

  /* Check if anything can be cleaned up:
    - if groups are not sorted
    - if there are any empty groups (no count or no weight)
    - if there are groups with same weight
    */
  canCleanUp() {
    if (this.plateGroups.length > 0) {
      if (this.plateGroups.some(g => !(g.count >= 1 && g.weight_kg > 0))) {
        return true;
      }
      let sorted = sortBy(this.plateGroups, ['weight_kg']);
      if (!isEqual(this.plateGroups, sorted)) {
        return true;
      }
      if (countSimilarValues(this.plateGroups.map(g => g.weight_kg)).length < this.plateGroups.length) {
        return true;
      }
    }
    for (let kind of RANGE_WEIGHT_KINDS) {
      if (rangeGroupsNeedCleanUp(this.rangeGroupsOf(kind))) {
        return true;
      }
    }
    return false;
  }

  enumerateFixedGroup(fixed: RangeWeightGroup) {
    let weights = expandRangeGroup(fixed);
    return weights.map((w: number) => formatMeasure(w, 'addedweight_kg', this.settings.locale, this.equipment.weight_unit)).join(', ');
  }

  enumerateStackGroup(fixed: RangeWeightGroup, stackIndex: number): { description: string, stackIndex: number } {
    let weights: number[] = expandRangeGroup(fixed);
    let formatArray = (a) => a.map((w: number, i: number) => `#${stackIndex + i}: ${formatMeasure(w, 'addedweight_kg', this.settings.locale, this.equipment.weight_unit)}`).join('<br/>')
    if (weights.length < 7) {
      return { description: formatArray(weights), stackIndex: stackIndex + weights.length };
    }

    let first = formatArray(weights.slice(0, 3));
    stackIndex += weights.length - 2;
    let last = formatArray(weights.slice(-2));

    return { description: `${first}<br/>...<br/>${last}`, stackIndex: stackIndex + 2 };

    //return weights.map((w: number, i: number) => `#${firstIndex + i}: ${formatMeasure(w, 'addedweight_kg', this.settings.locale, this.equipment.weight_unit)}`).join('<br/>');
  }

  getFixedSummary(fixed: RangeWeightGroup): string {
    return this.enumerateFixedGroup(fixed);
  }

  getStackedSummary(stack: RangeWeightGroup): string {
    // for stack summary, just show the weight range without indices
    return this.enumerateFixedGroup(stack);
  }

  updateTexts() {
    if (this.equipment.type == EquipmentType.Bar) {
      this.collars2x_text = formatMeasure(
        this.equipment.collar_kg * 2,
        'addedweight_kg',
        this.settings.locale,
        this.equipment.weight_unit,
      );
    }

    for (let g of this.fixedGroups) {
      g.description = this.enumerateFixedGroup(g);
    }

    console.log("Starting update")
    let lastStackIndex = 1;
    for (let i = 0; i < this.stackedGroups.length; i++) {
      let g = this.stackedGroups[i];
      let { description, stackIndex } = this.enumerateStackGroup(g, lastStackIndex);
      g.description = description;
      lastStackIndex = stackIndex;
    }
    console.log("Finished update")

    this.cdr.markForCheck();
  }

  getPlateCountConfig() {
    if (this.equipment.type === EquipmentType.Bar) {
      return [Config_Pairs];
    }
    return [Config_Plates];
  }

  getPlatesSummary(plateGroup: PlatesGroup): TranslatedString {
    const weight = formatMeasure(
      plateGroup.weight_kg,
      'addedweight_kg',
      this.settings.locale,
      this.equipment.weight_unit,
    );
    if (this.equipment.type === EquipmentType.Bar) {
      return LL.equipment.labels.platesSummaryPairs({
        count: plateGroup.count,
        weight
      });
    }
    return LL.equipment.labels.platesSummary({
      count: plateGroup.count,
      weight
    });
  }

  async onRowFocus(i: number) {
    let differentRow = this.focusedRow !== i;
    this.focusedRow = i;
    // if a new row is focused, the buttons will appear
    if (differentRow) {
      // make sure the click doesn't reach those buttons
      this.blockRowEdits = true;
      await sleep(100);
      this.blockRowEdits = false;
    }
  }

  userUnitChanged() {
    this.somethingChanged();
    this.updateTexts();
  }
  someWeightChanged() {
    this.somethingChanged();
  }

  somethingChanged() {
    this.updateTexts();

    // here we need to ungroup just to be able to compare, but must not re-group not to "snap" the UI itself
    this.ungroupWeights();
    this.isModified = !deepCompareEQ(this.equipment, this.equipmentInital) || this.createMode;
    this.cdr.markForCheck();
  }

  async exitPageAsync(opts: { save: boolean }) {
    if (opts.save && !deepCompareEQ(this.equipment, this.equipmentInital)) {
      await this.saveAsync();
    } else if (this.equipment && this.equipmentInital) {
      // only restore if both exist (not in create mode step 1)
      deepOverwrite(this.equipment, this.equipmentInital);
    }

    // Pass equipment ID to popAsync - NavController will invoke returnCallback on parent
    // (callback must come after saving, since saving sets up global exercise setup for this user)
    const resultId = opts.save ? this.equipment?.id : undefined;
    // Only NOW is the save-or-discard decision actually carried out, so only now
    // may the leave guard be told to stay quiet. Setting this any earlier would
    // strand the flag if the save above threw: the page would stay open with the
    // guard switched off, and the next Back press would discard the weights
    // silently — the very bug this guard exists to prevent.
    this.leaveWithoutAsking.set();
    await this.navCtrl.popAsync(resultId);
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  handleAcceptButton(event: Event) {
    lockClick(event, this.exitPageAsync({ save: true }));
  }

  handleCancelButton(event: Event) {
    lockClick(event, this.exitPageAsync({ save: false }));
  }

}

